/* global globalThis */
/**
 * @fileoverview Top Opportunities Component
 * @module components/opportunities/TopOpportunities
 * @description A reusable table component for displaying medication opportunities with sorting,
 * filtering, and various display modes for different contexts (member monitoring, priority outreach).
 *
 * @requires react
 * @requires prop-types
 * @requires @mui/material
 * @requires @mui/icons-material
 * @requires date-fns
 * @requires lodash
 * @requires ../../utils/capitalize
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { formatDateOnly } from "../../utils/formatDateTime";
import {
  LanguageRounded as LanguageRoundedIcon,
  WatchLaterRounded as WatchLaterRoundedIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/**
 * Calculates the screen diagonal size in inches
 * @private
 * @returns {number} Screen diagonal size in inches
 */
function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

/**
 * Returns color scheme based on status
 * @param {string} status - The status to get colors for
 * @returns {Object} Color scheme object
 * @property {string} bgColor - Background color
 * @property {string} borderColor - Border color
 * @property {string} iconColor - Icon/text color
 */

/**
 * Gets status information including colors based on category and status
 * @param {string} category - The category of the opportunity
 * @param {string} status - The status of the opportunity
 * @returns {Object} Status information object
 * @property {string} status - Formatted status text
 * @property {string} statusColor - Background color for status
 * @property {string} statusText - Text color for status
 * @property {string} borderColor - Border color for status
 * @property {string} categoryColor - Background color for category
 * @property {string} categoryText - Text color for category
 */
const DEFAULT_ICON_COLOR = "#403C6F";
const DEFAULT_BG_COLOR = "#ECECF1";
const DEFAULT_BORDER_COLOR = "#ECECF1";

const getStatusInfo = (category, status, colourCode) => {
  return {
    status: status || "Eligible",
    statusColor: colourCode || DEFAULT_BG_COLOR,
    statusText: DEFAULT_ICON_COLOR,
    borderColor: colourCode || DEFAULT_BORDER_COLOR,
    categoryColor: "#F3E5F5",
    categoryText: "#4A148C",
  };
};

const PRIORITY_OUTREACH_DATA = {
  memberCost: ["$45.99", "$32.50", "$67.25", "$89.99", "$12.75", "$24.99"],
  lastFilled: [
    "2 days ago",
    "1 week ago",
    "3 days ago",
    "5 days ago",
    "1 day ago",
    "2 weeks ago",
  ],
};
/**
 * TopOpportunities Component
 *
 * A table component that displays medication opportunities with various display modes
 * and interactive features like sorting and filtering.
 *
 * @param {Object} props - Component props
 * @param {Array} [props.opportunities=[]] - Array of opportunity objects to display
 * @param {('memberMonitoring'|'priorityOutreach')} [props.source='memberMonitoring'] - Display mode
 * @param {boolean} [props.showOutcome=false] - Whether to show outcome column
 * @returns {JSX.Element} The rendered component
 *
 * @example
 * <TopOpportunities
 *   opportunities={opportunityData}
 *   source="memberMonitoring"
 *   showOutcome={true}
 * />
 */

const TopPharmacyOpportunities = ({
  opportunities = [],
  source = "memberMonitoring",
  showOutcome = false,
}) => {
  const [containerScale, setContainerScale] = useState(1);

  const programStatusOrder = {
    eligible: 1,
    enrolled: 2,
    resolved: 3,
    "not resolved": 4,
    declined: 5,
  };

  const transformedOpportunities = [...opportunities]
    .map((opp, index) => {
      const monitoringReason = opp.monitoringReason || "N/A";

      const memberCost =
        opp.memberCost ||
        opp.cost ||
        (source === "priorityOutreach"
          ? PRIORITY_OUTREACH_DATA.memberCost[
              index % PRIORITY_OUTREACH_DATA.memberCost.length
            ]
          : "$0.00");

      let lastFilled;
      if (opp.lastFilled) {
        lastFilled = formatDateOnly(opp.lastFilled, { timeZone: "UTC" });
      } else if (source === "priorityOutreach") {
        lastFilled =
          PRIORITY_OUTREACH_DATA.lastFilled[
            index % PRIORITY_OUTREACH_DATA.lastFilled.length
          ];
      } else {
        lastFilled = "Not available";
      }

      const status = capitalizeFirstLetter(opp.status) || "";
      const statusInfo = getStatusInfo(opp.category, status, opp.colourCode);

      const transformedItem = {
        ...opp,
        name: opp.name,
        supply: "",
        dueDate: opp.dueDate
          ? formatDateOnly(opp.dueDate, { timeZone: "UTC" })
          : "",
        monitoringReason: monitoringReason,
        memberCost: memberCost,
        isGraduated: opp.isGraduated,
        lastFilled: lastFilled,
        ...statusInfo,
        medication: opp.drug || opp.medicationName,
        strength:
          opp.strength && opp.strength !== "N/A"
            ? opp.strength
            : opp.program?.strength || "",
        cost: memberCost,
        lastFilledDate: lastFilled,

        metricQuantity: opp.metricQuantity || opp.quantity || null,
        daysSupply: (() => {
          if (opp.program?.daySupply === undefined) {
            if (opp.daysSupply === undefined) {
              return null;
            } else {
              return Number(opp.daysSupply);
            }
          } else {
            return Number(opp.program.daySupply);
          }
        })(),
        pdc: opp.pdc || null,
        sig: opp.sig || null,
        potencyUnit: opp.potencyUnit || "",
      };
      return transformedItem;
    })
    .sort((a, b) => {
      if (a.isGraduated !== b.isGraduated) {
        return a.isGraduated ? 1 : -1;
      }

      const statusA = (a.status || "").toLowerCase();
      const statusB = (b.status || "").toLowerCase();
      const orderA = programStatusOrder[statusA] || 999;
      const orderB = programStatusOrder[statusB] || 999;
      return orderA - orderB;
    });
  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transform: `scale(${containerScale})`,
        transformOrigin: "top left",
        width: containerScale < 1 ? "111%" : "100%",
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          flex: 1,
          overflowX: "hidden",
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "#7F7C9F #f1f1f1",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            backgroundColor: "#7F7C9F !important",
            borderRadius: "4px",
            backgroundClip: "padding-box",
            border: "2px solid #7F7C9F",
            opacity: "1 !important",
            visibility: "visible !important",
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover":
            {
              backgroundColor: "#7F7C9F !important",
            },
        }}
      >
        <Table
          sx={{
            borderCollapse: "collapse",
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-root": {
              borderBottom: "1px solid #E0E0E0",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              borderBottom: "1px solid #E0E0E0",
              backgroundColor: "#fff",
              position: "sticky",
              top: 0,
              zIndex: 1,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  color: "#403C6F",
                  padding: "12px 8px",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #E0E0E0",
                  textAlign: "left",
                }}
              >
                Member Details
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: "#403C6F",
                  padding: "12px 8px",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #E0E0E0",
                  textAlign: "center",
                  minWidth: "200px",
                }}
              >
                Outreach Reason
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: "#403C6F",
                  padding: "12px 8px",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #E0E0E0",
                  textAlign: "center",
                  minWidth: "200px",
                }}
              >
                Due Date
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transformedOpportunities.length > 0 ? (
              transformedOpportunities.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  <TableRow
                    sx={{
                      verticalAlign: "top",
                      borderBottom: "1px solid #E0E0E0",
                      "&:last-child": {
                        borderBottom: "none",
                      },
                      "&.MuiTableRow-root": {
                        "&.Mui-selected": {
                          backgroundColor: "#FFFF",
                          "& .MuiTableCell-root": {
                            borderBottom: "1px solid #E0E0E0",
                            "&:first-of-type": {
                              borderTopLeftRadius: "8px",
                              borderBottomLeftRadius: "8px",
                            },
                            "&:last-child": {
                              borderTopRightRadius: "8px",
                              borderBottomRightRadius: "8px",
                            },
                          },
                        },
                      },
                    }}
                    selected={
                      item.category === "Biosimilars" &&
                      item.status === "Eligible"
                    }
                  >
                    <TableCell
                      align="left"
                      sx={{
                        color:
                          source === "memberMonitoring" ? "#403C6F" : "#128635",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "20px",
                        borderBottom: "1px solid #f0f0f0",
                        whiteSpace:
                          source === "memberMonitoring" ? "normal" : "nowrap",
                        textAlign: "left",
                        padding: "8px 4px",
                        minWidth: "auto",
                        width: "auto",
                        boxSizing: "border-box",
                        verticalAlign: "middle",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "2px",
                        }}
                      >
                        <Tooltip
                          title={item.name || ""}
                          placement="bottom-start"
                          arrow
                          describeChild
                          disableInteractive
                          slotProps={{
                            popper: {
                              sx: {
                                "& .MuiTooltip-tooltip": {
                                  backgroundColor: "#1c1c1f",
                                  color: "#FFFFFF",
                                  margin: 0,
                                  fontSize: "12px",
                                  lineHeight: 1.3,
                                },
                                "& .MuiTooltip-arrow": {
                                  color: "#1c1c1f",
                                  margin: 0,
                                  left: "50% !important",
                                  transform: "translateX(-50%) !important",
                                },
                              },
                            },
                            tooltip: {
                              sx: {
                                margin: 0,
                                marginTop: "0px !important",
                                padding: "4px 8px",
                              },
                            },
                          }}
                        >
                          <Link
                            style={{
                              fontWeight: 600,
                              fontStyle: "Bold",
                              fontSize: "17px",
                              color: "#4b3f72",
                              flexDirection: "column",
                              textDecoration: "none",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textDecorationSkipInk: "auto",
                              textUnderlineOffset: 3,
                              cursor: "pointer",
                              flex: 1,
                              minWidth: "100px",
                              display: "inline-block",
                              marginTop: "4px",
                            }}
                          >
                            {item.name}
                          </Link>
                        </Tooltip>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 2,
                            fontWeight: 500,
                            fontSize: "11px",
                            color: "#403C6F",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <LanguageRoundedIcon
                              style={{ fontSize: 14, color: "#1B2F3F" }}
                            />
                            <span style={{ fontSize: "11px" }}>
                              {item.language}
                            </span>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              ml: 1,
                            }}
                          >
                            <WatchLaterRoundedIcon
                              style={{ fontSize: 14, color: "#1B2F3F" }}
                            />
                            <span style={{ fontSize: "11px" }}>
                              {item.timezone}
                            </span>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        padding: "8px 4px",
                        borderBottom: "none",
                        verticalAlign: "middle",
                        width: "200px",
                        minWidth: "200px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          justifyContent: "center",
                          minHeight: "40px",
                          alignItems: "center",
                        }}
                      >
                        {item.outreachReason ? (
                          item.outreachReason.split(",").map((reason, idx) => (
                            <Chip
                              key={`${item.id || item.opportunityId || "reason"}-${reason.trim()}-${idx}`}
                              label={reason.trim()}
                              size="small"
                              sx={{
                                backgroundColor: "#F0F0F7",
                                color: "#403C6F",
                                fontWeight: 500,
                                fontSize: "12px",
                                height: "27px",
                                borderRadius: "12px",
                                "& .MuiChip-label": {
                                  px: 1,
                                  py: 0.5,
                                },
                              }}
                            />
                          ))
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#9E9E9E",
                              fontStyle: "italic",
                            }}
                          >
                            N/A
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "8px 4px",
                        borderBottom: "none",
                        verticalAlign: "middle",
                        width: "100px",
                        minWidth: "100px",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#403C6F",
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          width: "100%",
                          display: "block",
                          minHeight: "40px",
                        }}
                      >
                        {item.dueDate || "N/A"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    source === "memberMonitoring" || !showOutcome ? 2 : 3
                  }
                  align="center"
                  sx={{ color: "#7F7C9F", py: 3 }}
                >
                  No opportunities available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

TopPharmacyOpportunities.propTypes = {
  opportunities: PropTypes.array,
  source: PropTypes.oneOf(["memberMonitoring", "priorityOutreach"]),
  showOutcome: PropTypes.bool,
};
TopPharmacyOpportunities.defaultProps = {
  source: "memberMonitoring",
};
export default TopPharmacyOpportunities;
